import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

// "LLM $" tab of Usage Stats (Anthony, 2026-10-31): the same shape as
// Mackey's "LLM cost and the cost of a green ticket".

interface ModelRow {
  label: string;
  cost: number;
  calls: number;
  answers: number;
  system_green: number;
  judge_green: number;
  cost_per_system_green: number | null;
  cost_per_judge_green: number | null;
}

interface GreenRun {
  ticket_key: string;
  written_at: string;
  model: string | null;
  judge: string | null;
}

interface LlmCost {
  recorded: boolean;
  period: string;
  recording_since: string | null;
  models: ModelRow[];
  earlier: ModelRow | null;
  total: ModelRow;
  per_day: { day: string; cost: number }[];
  green_runs: GreenRun[];
  cheapest_green: string | null;
  most_judge_greens: string | null;
  unpriced_models: string[];
}

type Period = 'today' | '7d' | '30d' | 'all';
const PERIODS: { key: Period; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All' },
];

// Small amounts keep three decimals, as in Mackey ($0.051).
const usd = (v: number | null | undefined) =>
  v == null ? '-' : v < 1 ? `$${v.toFixed(3)}` : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n: number, of: number) => (of > 0 ? ` (${Math.round((100 * n) / of)}%)` : '');

const th = 'px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200';
const td = 'px-3 py-2 text-slate-900 dark:text-slate-100';
const card = 'rounded-lg border border-slate-200 dark:border-slate-700 p-4';
const small = 'text-xs text-slate-500 dark:text-slate-400';

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className={small}>{label}</div>
    <div className="text-lg font-semibold text-slate-900 dark:text-white">{value}</div>
  </div>
);

export const LlmCostSection: React.FC = () => {
  const [period, setPeriod] = useState<Period>('all');
  const [data, setData] = useState<LlmCost | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetchApi(`${API_BASE}/admin/usage-stats/llm-cost?period=${period}`, { headers });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const btn = (p: Period) =>
    `px-3 py-1.5 text-sm rounded border ${
      period === p
        ? 'bg-blue-600 border-blue-600 text-white'
        : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300'
    }`;

  const rowsForTable = data ? [...data.models, ...(data.earlier ? [data.earlier] : []), data.total] : [];

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">LLM cost and the cost of a green ticket</h2>
      <p className={`${small} mb-3`}>
        Approximate: token counts times assumed prices. The real bill can differ.
        {data?.recording_since && ` Token record starts ${new Date(data.recording_since).toLocaleString()}.`}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {PERIODS.map((p) => (
          <button key={p.key} className={btn(p.key)} onClick={() => setPeriod(p.key)}>
            {p.label}
          </button>
        ))}
        <button className={btn('all').replace('bg-blue-600 border-blue-600 text-white', '')} onClick={load}>
          Refresh
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-slate-500 mb-3">Loading…</p>}
      {data && !data.recorded && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          No token record yet: migration 067 has not run.
        </p>
      )}

      {data?.recorded && (
        <>
          <div className={`${card} grid grid-cols-2 md:grid-cols-5 gap-4 mb-3`}>
            <Stat label="Total spent" value={usd(data.total.cost)} />
            <Stat label="Answers written" value={data.total.answers.toLocaleString()} />
            <Stat label="System green" value={`${data.total.system_green}${pct(data.total.system_green, data.total.answers)}`} />
            <Stat label="Judge green" value={String(data.total.judge_green)} />
            <Stat label="Cost per judge green" value={usd(data.total.cost_per_judge_green)} />
          </div>
          {(data.cheapest_green || data.most_judge_greens) && (
            <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
              {data.cheapest_green && `Cheapest green so far: ${data.cheapest_green}. `}
              {data.most_judge_greens && `Most judge greens per answer (5 answers or more): ${data.most_judge_greens}.`}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {data.models.map((m) => (
              <div key={m.label} className={card}>
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold text-slate-900 dark:text-white">{m.label}</span>
                  <span className="text-lg font-semibold text-slate-900 dark:text-white">{usd(m.cost)}</span>
                </div>
                <p className={`${small} mb-2`}>
                  {m.calls.toLocaleString()} calls · {m.answers.toLocaleString()} answers written
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Stat label="System green" value={`${m.system_green}${pct(m.system_green, m.answers)}`} />
                  <Stat label="Judge green" value={String(m.judge_green)} />
                  <Stat label="Cost per system green" value={usd(m.cost_per_system_green)} />
                  <Stat label="Cost per judge green" value={usd(m.cost_per_judge_green)} />
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto mb-2">
            <table className="w-full text-sm border border-slate-200 dark:border-slate-700">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th className={th}>Model</th><th className={th}>Cost</th><th className={th}>Calls</th>
                  <th className={th}>Answers</th><th className={th}>System green</th><th className={th}>Judge green</th>
                  <th className={th}>Cost per system green</th><th className={th}>Cost per judge green</th>
                </tr>
              </thead>
              <tbody>
                {rowsForTable.map((m) => {
                  const earlier = m === data.earlier;
                  return (
                    <tr key={m.label} className="border-t border-slate-200 dark:border-slate-700">
                      <td className={`${td} ${m === data.total ? 'font-semibold' : ''}`}>{m.label}</td>
                      <td className={td}>{earlier ? '-' : usd(m.cost)}</td>
                      <td className={td}>{earlier ? '-' : m.calls.toLocaleString()}</td>
                      <td className={td}>{m.answers.toLocaleString()}</td>
                      <td className={td}>{m.system_green}{pct(m.system_green, m.answers)}</td>
                      <td className={td}>{m.judge_green}</td>
                      <td className={td}>{usd(m.cost_per_system_green)}</td>
                      <td className={td}>{usd(m.cost_per_judge_green)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.earlier && (
            <p className={`${small} mb-2`}>
              Answers saved before 30 Oct 2026 have no recorded model and no token record. They are shown as their
              own row, "Earlier runs". Only the latest answer of each ticket from before {data.recording_since ? 'the token record' : 'migration 067'} is counted.
            </p>
          )}
          {data.unpriced_models.length > 0 && (
            <p className={`${small} mb-2`}>Calls with no price (counted as $0): {data.unpriced_models.join(', ')}.</p>
          )}

          <h3 className="font-semibold text-slate-900 dark:text-white mt-6 mb-2">Cost per day</h3>
          {data.per_day.length === 0 ? (
            <p className={small}>No recorded calls in this period.</p>
          ) : (
            <table className="text-sm border border-slate-200 dark:border-slate-700 mb-4">
              <tbody>
                {data.per_day.map((d) => (
                  <tr key={d.day} className="border-t border-slate-200 dark:border-slate-700">
                    <td className={td}>{d.day}</td>
                    <td className={`${td} text-right`}>{usd(d.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 className="font-semibold text-slate-900 dark:text-white mt-6 mb-2">Green runs and the model that wrote them</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 dark:border-slate-700">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th className={th}>Ticket</th><th className={th}>Saved</th><th className={th}>Model</th>
                  <th className={th}>System</th><th className={th}>Judge</th>
                </tr>
              </thead>
              <tbody>
                {data.green_runs.map((g) => (
                  <tr key={`${g.ticket_key}-${g.written_at}`} className="border-t border-slate-200 dark:border-slate-700">
                    <td className={td}>{g.ticket_key}</td>
                    <td className={td}>{new Date(g.written_at).toLocaleString()}</td>
                    <td className={td}>{g.model ?? 'not recorded'}</td>
                    <td className={td}>green</td>
                    <td className={td}>{g.judge ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
